import { useAppSelector } from 'app/hooks';
import { selectIsEditMode } from 'features/common/editModeSlice';
import { useRef, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import ReactPlayer from 'react-player';
import AlertModal from '../AlertModal';
import ConfirmModal from '../ConfirmModal';
import EditModeModalTitleBar from '../EditModeModalTitleBar';
import useGlobalModal from '../GlobalModal/useGlobalModal';
import './style.scss';
import videoUploadModalService from './videoUploadModalService';

export interface VideoUploadModalProps {
  videopath?: string;
  maxFileSize: number;
  onReflect?: (filename: string, videopath: string) => void;
  onClose?: () => void;
}

const VideoUploadModal = ({ videopath = '', maxFileSize, onReflect, onClose }: VideoUploadModalProps) => {
  const { t } = useTranslation();
  const { showGlobalModal, closeGlobalModal } = useGlobalModal();

  // selectors
  const isEditMode = useAppSelector(selectIsEditMode);

  // states
  const [currentVideoPath, setCurrentVideoPath] = useState(videopath);
  const [tempVideoPath, setTempVideoPath] = useState<string | null>(null);
  const [fileType, setFileType] = useState<string | null>('');
  const inputFileRef = useRef<HTMLInputElement>(null);
  const uploadFileRef = useRef<File | null>(null);

  const handleClose = () => {
    setTempVideoPath(null);
    onClose?.();
    closeGlobalModal?.();
  };

  const handleSelectFile = () => {
    if (inputFileRef.current) {
      inputFileRef.current.value = '';
      inputFileRef.current.click();
    }
  };

  const checkMP4MagicBytes = async (file: File) => {
    const arrayBuffer = await file.slice(0, 8).arrayBuffer();
    const bytes = new Uint8Array(arrayBuffer);
    return bytes[4] === 0x66 && bytes[5] === 0x74 && bytes[6] === 0x79 && bytes[7] === 0x70;
  };

  const checkUnwantedFileType = async (file: File) => {
    const arrayBuffer = await file.slice(0, 2).arrayBuffer();
    const bytes = new Uint8Array(arrayBuffer);
    const isExe = bytes[0] === 0x4d && bytes[1] === 0x5a;
    const isText = String.fromCharCode(bytes[0]).match(/[a-zA-Z0-9]/);
    return isExe || isText;
  };

  const handleFileChange = async () => {
    if (inputFileRef.current && inputFileRef.current.files) {
      const file = inputFileRef.current.files[0];
      const sizeInMB = file.size / (1024 * 1024);

      if (file) {
        const rFilter = /^(image\/jpeg|image\/png|image\/jpg)$/i;
        const vFilter =
          /^(video\/mp4|video\/quicktime|video\/x-msvideo|video\/x-matroska|video\/x-ms-wmv|video\/x-flv|video\/webm|video\/mp2t)$/i;
        const isRealVideoFile = await checkMP4MagicBytes(file);
        const isUnwantedFile = await checkUnwantedFileType(file);

        if ((vFilter.test(file.type) || isRealVideoFile) && !isUnwantedFile) {
          setFileType('video');
        } else if (rFilter.test(file.type) && !isUnwantedFile) {
          setFileType('image');
        } else {
          showGlobalModal(() => (
            <AlertModal title={t('App.warning')} message={t('VideoUploadModal.warn_upload_file_format')} />
          ));
          inputFileRef.current.value = '';
          setFileType(null);
          setTempVideoPath(null);
          return;
        }

        if (sizeInMB > maxFileSize && maxFileSize !== 0) {
          showGlobalModal(() => (
            <AlertModal
              title={t('App.warning')}
              message={t('VideoUploadModal.warn_upload_filesize').replace('{0}', `${maxFileSize}`)}
            />
          ));
          inputFileRef.current.value = '';
          return;
        }

        setTempVideoPath(URL.createObjectURL(file));
        uploadFileRef.current = file;
      }
    }
  };

  const handleReflect = async () => {
    if (uploadFileRef.current) {
      const bodyFormData = new FormData();
      bodyFormData.append('video_url', uploadFileRef.current, uploadFileRef.current.name);

      try {
        const response = await videoUploadModalService.uploadVideo(bodyFormData);
        const videopath = response.data.url;
        const filename = response.data.filename;
        onReflect && onReflect(filename, videopath);
        setCurrentVideoPath(tempVideoPath || '');
        setTempVideoPath(null);
        handleClose();
      } catch (e) {
        if (inputFileRef.current) inputFileRef.current.value = '';
      }
    }
  };

  const handleConfirmDelete = () => {
    if (currentVideoPath.length > 0) {
      showGlobalModal(() => (
        <ConfirmModal
          title={t('App.warning')}
          message={t('VideoUploadModal.warn_confirm_delete')}
          onConfirm={handleDelete}
        />
      ));
    }
  };

  const handleDelete = () => {
    setCurrentVideoPath('');
    onReflect && onReflect('', '');
  };

  const isVideo = currentVideoPath && typeof currentVideoPath === 'string' && currentVideoPath.includes('mp4');
  const isImage =
    currentVideoPath &&
    typeof currentVideoPath === 'string' &&
    (currentVideoPath.includes('jpg') || currentVideoPath.includes('png') || currentVideoPath.includes('jpeg'));

  return (
    <>
      {/* Modal select video */}
      <Modal size="sm" dialogClassName="upload-video-modal" show backdrop="static" centered onHide={handleClose}>
        <EditModeModalTitleBar title={t('VideoUploadModal.title')} />
        <div className={isEditMode ? 'reedit-mode-wrapper' : undefined}>
          <Modal.Body>
            <section>
              <div className="py-2 px-3 bg-violet mb-1 fw-bold">{t('VideoUploadModal.current_video')}</div>
              <div className="preview-video text-center">
                {currentVideoPath && isVideo ? (
                  <ReactPlayer url={currentVideoPath} controls width="100%" height="auto" />
                ) : currentVideoPath && isImage ? (
                  <img src={currentVideoPath} alt="" />
                ) : (
                  <div className="no-video d-flex align-items-center justify-content-center">No Video</div>
                )}
              </div>
              <div className="my-1 text-end">
                <Button className="btn--red" onClick={handleConfirmDelete}>
                  {t('VideoUploadModal.delete')}
                </Button>
              </div>
            </section>
            <section>
              <div className="py-2 px-3 bg-violet fw-bold">{t('VideoUploadModal.re_upload')}</div>
              <div className="p-3 text-center">
                <Button variant="primary" onClick={handleSelectFile}>
                  {t('VideoUploadModal.file_selection')}
                </Button>
                <input
                  ref={inputFileRef}
                  type="file"
                  className="file-video-url"
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                />
              </div>
            </section>
          </Modal.Body>

          <Modal.Footer className="justify-content-center">
            <Button variant="light" onClick={handleClose}>
              {t('VideoUploadModal.close')}
            </Button>
          </Modal.Footer>
        </div>
      </Modal>
      {/* Modal confirm video */}
      <Modal size="sm" className="confirm-upload-modal" show={!!tempVideoPath} backdrop="static" centered>
        <Modal.Header className="bg-primary justify-content-center">
          <Modal.Title className="fs-6 text-white">{t('VideoUploadModal.upload_confirmation')}</Modal.Title>
        </Modal.Header>

        <div className={isEditMode ? 'reedit-mode-wrapper' : undefined}>
          <Modal.Body>
            <div className="preview-video text-center">
              {tempVideoPath && fileType === 'video' ? (
                <ReactPlayer url={tempVideoPath} controls width="100%" height="auto" />
              ) : tempVideoPath && fileType === 'image' ? (
                <img src={tempVideoPath} alt="" />
              ) : (
                <div className="no-video d-flex align-items-center justify-content-center">No Video</div>
              )}
            </div>
          </Modal.Body>

          <Modal.Footer className={`justify-content-center`}>
            <Button variant="light" className="me-3" onClick={() => setTempVideoPath(null)}>
              {t('VideoUploadModal.close')}
            </Button>
            <Button variant="primary" onClick={handleReflect}>
              {t('VideoUploadModal.reflect')}
            </Button>
          </Modal.Footer>
        </div>
      </Modal>
    </>
  );
};

export default VideoUploadModal;
